import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { env } from '@/config/env';
import {
  DEFAULT_LOCALE,
  I18N_NAMESPACES,
  type Locale,
  LOCALE_DIRECTIONS,
  SUPPORTED_LOCALES,
} from '@/constants/locales';
import { logger } from '@/lib/logger';

import { detectLocale } from './detectLocale';
import { enResources } from './resources';

export const i18n = i18next.createInstance();

/**
 * No `.use(LanguageDetector)`: `lng` is supplied by our own pure `detectLocale`, so there is no
 * detector plugin and therefore no cache configuration that could write to the device (ADR-006,
 * ADR-014).
 */
export async function initI18n(
  search: string = window.location.search,
  navigatorLanguages: readonly string[] = navigator.languages,
): Promise<Locale> {
  const lng = detectLocale(search, navigatorLanguages);

  if (i18n.isInitialized) {
    await i18n.changeLanguage(lng);
    applyDocumentLanguage(lng);
    return lng;
  }

  await i18n.use(initReactI18next).init({
    lng,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    // Second line of defence: `detectLocale` already normalizes region tags, but a `?lang=pt-BR`
    // reaching `changeLanguage` directly from the footer switcher does not go through it.
    nonExplicitSupportedLngs: true,
    defaultNS: 'common',
    ns: [...I18N_NAMESPACES],
    // Safe only because React escapes text children. It does not license
    // `dangerouslySetInnerHTML`, which appears nowhere in this app (ADR-017).
    interpolation: { escapeValue: false },
    // A missing key yields '' rather than null, so `t` stays typed `string`.
    returnNull: false,
    resources: { en: enResources },
    debug: false,
    // Suspense is handled by our own boundaries with branded skeletons; i18next's own suspense
    // integration would render an unbranded blank frame on the first paint.
    react: { useSuspense: false },
  });

  applyDocumentLanguage(lng);
  i18n.on('languageChanged', (next) => {
    applyDocumentLanguage(next);
  });

  return lng;
}

/**
 * `lang` drives screen-reader pronunciation and `dir` drives layout. Both are set from one place, so
 * adding a right-to-left locale is a data change in `LOCALE_DIRECTIONS` (ADR-031).
 */
function applyDocumentLanguage(raw: string): void {
  const locale = (SUPPORTED_LOCALES as readonly string[]).includes(raw)
    ? (raw as Locale)
    : DEFAULT_LOCALE;
  document.documentElement.lang = locale;
  document.documentElement.dir = LOCALE_DIRECTIONS[locale];
}

/** The language actually in effect, narrowed to our union for `Intl` and `Accept-Language`. */
export function currentLocale(): Locale {
  const raw = i18n.language === '' ? DEFAULT_LOCALE : i18n.language;
  const base = raw.split('-')[0];
  return base !== undefined && (SUPPORTED_LOCALES as readonly string[]).includes(base)
    ? (base as Locale)
    : DEFAULT_LOCALE;
}

if (!env.isProduction) {
  // The parity test is the real gate; this only shortens the loop while writing a screen, since a
  // missing key renders as a blank label rather than as an error.
  i18n.on('missingKey', (lngs, namespace, key) => {
    logger.warn('[i18n] missing key', { lngs, namespace, key });
  });
}
